import type { TrainerId } from "./trainers.ts";

export const ITEM_IDS = [
  "capsule",
  "gloss",
  "myth",
  "sip",
  "tallsip",
  "fullcope",
  "sticker",
  "frame",
  "cleartea",
  "dunewrap",
  "neonjacket",
  "blossomcoat",
] as const;

export type ItemId = (typeof ITEM_IDS)[number];

export interface ItemDef {
  id: ItemId;
  name: string;
  price: number;
  kind: "capsule" | "heal" | "cosmetic" | "cleanse";
  requiresTrainer?: TrainerId;
  /** A regional cosmetic sells only at the stall in this zone. */
  stall?: "frontierPost" | "neonHarbor" | "bridgeport";
  heal?: number;
  /** Heals this share of max HP instead of a flat amount, so it keeps up with the level. */
  healPct?: number;
  catchMul?: number;
  desc: string;
}

export const ITEMS: Record<ItemId, ItemDef> = {
  capsule: {
    id: "capsule",
    name: "Meme Tin",
    price: 200,
    kind: "capsule",
    catchMul: 1,
    desc: "Standard latch. You throw a can at a vibe. That is the bit.",
  },
  gloss: {
    id: "gloss",
    name: "Gloss Tin",
    price: 600,
    kind: "capsule",
    catchMul: 1.6,
    desc: "Polished lip. They stare long enough to get caught. Unfair. The stall knows.",
  },
  myth: {
    id: "myth",
    name: "Myth Tin",
    price: 1400,
    kind: "capsule",
    catchMul: 2.3,
    desc: "Rumor metal. The label promises a legend. The latch signed in ink.",
  },
  sip: {
    id: "sip",
    name: "Sip Potion",
    price: 150,
    kind: "heal",
    heal: 22,
    desc: "A short slurp from the dépanneur fridge. Mends 22 HP. Best before was a suggestion.",
  },
  tallsip: {
    id: "tallsip",
    name: "Tall Sip",
    price: 400,
    kind: "heal",
    healPct: 0.5,
    desc: "The big bottle from the back of the fridge. Mends half a partner's HP, at any level.",
  },
  fullcope: {
    id: "fullcope",
    name: "Full Cope",
    price: 500,
    kind: "heal",
    heal: 999,
    desc: "One deep inhale. The kettle called and every HP answered. One partner, all the way back.",
  },
  sticker: {
    id: "sticker",
    name: "Sticker Pack",
    price: 300,
    kind: "cosmetic",
    desc: "Peel-on Scout decals. Pure shirt flex. The take ages out; the sticker does not.",
  },
  frame: {
    id: "frame",
    name: "Frame Tint",
    price: 800,
    kind: "cosmetic",
    desc: "A gold lip for the pocket HUD. Pure decoration. Your HP bar will not notice.",
  },
  dunewrap: {
    id: "dunewrap",
    name: "Dune Wrap",
    price: 6000,
    kind: "cosmetic",
    stall: "frontierPost",
    desc: "Sand-colored layers and a sun band. Frontier Post tailoring. Pure style; no stat will notice.",
  },
  neonjacket: {
    id: "neonjacket",
    name: "Neon Jacket",
    price: 12000,
    kind: "cosmetic",
    stall: "neonHarbor",
    desc: "Harbor night-market jacket with pink piping that glows in screenshots. Pure style.",
  },
  blossomcoat: {
    id: "blossomcoat",
    name: "Blossom Coat",
    price: 18000,
    kind: "cosmetic",
    stall: "bridgeport",
    desc: "Petal-pink coat from the Sidechain looms, with a green pack. Pure style, very pink.",
  },
  cleartea: {
    id: "cleartea",
    name: "Clear Tea",
    price: 250,
    kind: "cleanse",
    requiresTrainer: "ilex",
    desc: "In battle: clears stat drops, keeps the good ones. Costs a turn. The kettle does not refund.",
  },
};

export const SHOP_STOCK: ItemId[] = [
  "capsule",
  "gloss",
  "myth",
  "sip",
  "tallsip",
  "fullcope",
  "sticker",
  "frame",
  "cleartea",
];

export const CURRENCY = "coins";

/** Outfits a scout can wear (the Sticker Pack is the first one); "" means the default look. */
export const OUTFIT_IDS = ["sticker", "dunewrap", "neonjacket", "blossomcoat"] as const;
export type OutfitId = (typeof OUTFIT_IDS)[number];

/** What a stall in this zone sells: the shared stock, then that region's own cosmetic. */
export function stallStock(zone: string): ItemId[] {
  return [...SHOP_STOCK, ...ITEM_IDS.filter((id) => ITEMS[id].stall === zone)];
}

/** HP a heal item restores to a partner with this max HP. */
export function itemHeal(def: ItemDef, max: number): number {
  return def.healPct ? Math.max(1, Math.ceil(max * def.healPct)) : def.heal ?? 22;
}
